// UltraFlex Admin - Staff Management (Staff CRUD, Rota, Shifts, Holidays, Permissions, QR)
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGate from '../../components/PermissionGate';
import QRCode from 'react-qr-code';
import {
  Users, Calendar, Clock, Umbrella, Plus, Check, X, Building2, UserCog,
  Shield, Search, Mail, Phone, Hash, MapPin, ChevronLeft, ChevronRight,
  Eye, Edit3, Trash2, QrCode, StickyNote, Pin, PinOff, Key
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_PERMISSIONS = [
  { key: 'view_members', label: 'View Members', group: 'Members' },
  { key: 'search_members', label: 'Search Members', group: 'Members' },
  { key: 'update_member', label: 'Edit Members', group: 'Members' },
  { key: 'view_access_logs', label: 'View Access Logs', group: 'Access' },
  { key: 'grant_temporary_access', label: 'Grant Temp Access', group: 'Access' },
  { key: 'view_site_dashboard', label: 'View Site Dashboard', group: 'Dashboard' },
  { key: 'view_site_reports', label: 'View Site Reports', group: 'Reports' },
  { key: 'view_estate_dashboard', label: 'View Estate Dashboard', group: 'Dashboard' },
  { key: 'view_estate_reports', label: 'View Estate Reports', group: 'Reports' },
  { key: 'manage_plans', label: 'Manage Plans', group: 'Billing' },
  { key: 'view_all_payments', label: 'View All Payments', group: 'Billing' },
  { key: 'process_refund', label: 'Process Refunds', group: 'Billing' },
  { key: 'export_finance_reports', label: 'Finance Export', group: 'Finance' },
  { key: 'manage_staff', label: 'Manage Staff', group: 'Admin' },
  { key: 'manage_crm', label: 'Manage CRM', group: 'CRM' },
  { key: 'manage_assets', label: 'Manage Assets', group: 'Assets' },
];

const AdminStaffManagement = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('staff');
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [rota, setRota] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1);
    return d.toISOString().split('T')[0];
  });
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [showAddShift, setShowAddShift] = useState(false);
  const [showAddRota, setShowAddRota] = useState(false);
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [showPermModal, setShowPermModal] = useState(null);
  const [showQR, setShowQR] = useState(null);
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [staffNotes, setStaffNotes] = useState([]);
  const [newStaff, setNewStaff] = useState({ first_name: '', last_name: '', email: '', phone: '', role: 'staff', site_id: '', permissions: [] });
  const [newShift, setNewShift] = useState({ name: '', start_time: '06:00', end_time: '14:00' });
  const [newRota, setNewRota] = useState({ staff_id: '', shift_id: '', date: '', notes: '' });
  const [newHoliday, setNewHoliday] = useState({ start_date: '', end_date: '', reason: '' });
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = user?.role === 'super_admin' || user?.role === 'developer_admin' || user?.role === 'admin';
  const isManager = isAdmin || user?.role === 'manager';
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { fetchSites(); }, []);
  useEffect(() => { if (activeTab === 'staff') fetchStaff(); }, [activeTab, selectedSite, searchQuery]);
  useEffect(() => { if (activeTab === 'shifts') fetchShifts(); }, [activeTab, selectedSite]);
  useEffect(() => { if (activeTab === 'rota') { fetchShifts(); fetchStaff(); fetchRota(); } }, [activeTab, selectedSite, weekStart]);
  useEffect(() => { if (activeTab === 'holidays') fetchHolidays(); }, [activeTab, selectedSite]);

  const fetchSites = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sites`, { headers });
      const data = await res.json();
      setSites(data.items || []);
    } catch (e) { console.error(e); }
  };

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSite) params.append('site_id', selectedSite);
      const res = await fetch(`${API_URL}/api/staff-management/staff?${params}`, { headers });
      const data = await res.json();
      let items = data.items || [];
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        items = items.filter(s => `${s.first_name} ${s.last_name} ${s.email} ${s.employee_number || ''}`.toLowerCase().includes(q));
      }
      setStaff(items);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchShifts = async () => {
    try {
      const params = selectedSite ? `?site_id=${selectedSite}` : '';
      const res = await fetch(`${API_URL}/api/staff-management/shifts${params}`, { headers });
      setShifts(await res.json() || []);
    } catch (e) { console.error(e); }
  };

  const fetchRota = async () => {
    if (!selectedSite) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff-management/rota?site_id=${selectedSite}&week_start=${weekStart}`, { headers });
      setRota(await res.json() || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSite) params.append('site_id', selectedSite);
      const res = await fetch(`${API_URL}/api/staff-management/holidays?${params}`, { headers });
      const data = await res.json();
      setHolidays(data.items || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchStaffNotes = async (staffId) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/notes/staff/${staffId}`, { headers });
      if (res.ok) setStaffNotes(await res.json());
      else setStaffNotes([]);
    } catch (e) { setStaffNotes([]); }
  };

  const createStaffMember = async () => {
    if (!newStaff.first_name || !newStaff.email) return;
    try {
      const res = await fetch(`${API_URL}/api/staff`, { method: 'POST', headers, body: JSON.stringify(newStaff) });
      if (!res.ok) { const d = await res.json(); alert(d.detail || 'Failed'); return; }
      setShowAddStaff(false);
      setNewStaff({ first_name: '', last_name: '', email: '', phone: '', role: 'staff', site_id: '', permissions: [] });
      fetchStaff();
    } catch (e) { alert('Failed to create staff'); }
  };

  const deactivateStaff = async (id) => {
    if (!window.confirm('Deactivate this staff member?')) return;
    await fetch(`${API_URL}/api/staff/${id}`, { method: 'DELETE', headers });
    fetchStaff();
    if (selectedStaff?.id === id) setSelectedStaff(null);
  };

  const [editingStaff, setEditingStaff] = useState(null);
  const saveStaffEdit = async () => {
    if (!editingStaff) return;
    const res = await fetch(`${API_URL}/api/staff/${editingStaff.id}`, { method: 'PATCH', headers, body: JSON.stringify({ first_name: editingStaff.first_name, last_name: editingStaff.last_name, phone: editingStaff.phone, role: editingStaff.role, site_id: editingStaff.site_id }) });
    if (res.ok) { const updated = await res.json(); setSelectedStaff(updated); setEditingStaff(null); fetchStaff(); }
    else { const d = await res.json().catch(() => ({})); alert(d.detail || 'Failed to update'); }
  };

  const forcePasswordReset = async (id) => {
    if (!window.confirm('Force this staff member to reset their password on next login?')) return;
    const res = await fetch(`${API_URL}/api/staff/${id}/force-password-reset`, { method: 'POST', headers });
    if (res.ok) alert('Password reset flagged. Staff will be prompted on next login.');
    else alert('Failed to flag password reset.');
  };

  const deleteShift = async (id) => {
    if (!window.confirm('Delete this shift?')) return;
    await fetch(`${API_URL}/api/staff-management/shifts/${id}`, { method: 'DELETE', headers });
    fetchShifts();
  };

  const createShift = async () => {
    if (!selectedSite || !newShift.name) return;
    await fetch(`${API_URL}/api/staff-management/shifts`, { method: 'POST', headers, body: JSON.stringify({ ...newShift, site_id: selectedSite }) });
    setShowAddShift(false);
    setNewShift({ name: '', start_time: '06:00', end_time: '14:00' });
    fetchShifts();
  };

  const createRotaEntry = async () => {
    if (!selectedSite || !newRota.staff_id || !newRota.date) return;
    await fetch(`${API_URL}/api/staff-management/rota`, { method: 'POST', headers, body: JSON.stringify({ ...newRota, site_id: selectedSite }) });
    setShowAddRota(false);
    setNewRota({ staff_id: '', shift_id: '', date: '', notes: '' });
    fetchRota();
  };

  const deleteRotaEntry = async (id) => {
    await fetch(`${API_URL}/api/staff-management/rota/${id}`, { method: 'DELETE', headers });
    fetchRota();
  };

  const createHolidayRequest = async () => {
    if (!newHoliday.start_date || !newHoliday.end_date) return;
    await fetch(`${API_URL}/api/staff-management/holidays`, { method: 'POST', headers, body: JSON.stringify({ ...newHoliday, site_id: selectedSite }) });
    setShowAddHoliday(false);
    setNewHoliday({ start_date: '', end_date: '', reason: '' });
    fetchHolidays();
  };

  const reviewHoliday = async (id, action) => {
    await fetch(`${API_URL}/api/staff-management/holidays/${id}/review`, { method: 'POST', headers, body: JSON.stringify({ action }) });
    fetchHolidays();
  };

  const updatePermissions = async (staffId, perms) => {
    await fetch(`${API_URL}/api/staff-management/staff/${staffId}/permissions`, { method: 'PATCH', headers, body: JSON.stringify({ permissions: perms }) });
    setShowPermModal(null);
    fetchStaff();
  };

  const addNote = async () => {
    if (!noteContent.trim() || !selectedStaff) return;
    await fetch(`${API_URL}/api/crm/notes/staff/${selectedStaff.id}`, {
      method: 'POST', headers, body: JSON.stringify({ content: noteContent, note_type: noteType })
    });
    setNoteContent('');
    setNoteType('general');
    setShowAddNote(false);
    fetchStaffNotes(selectedStaff.id);
  };

  const deleteNote = async (noteId) => {
    await fetch(`${API_URL}/api/crm/notes/${noteId}`, { method: 'DELETE', headers });
    fetchStaffNotes(selectedStaff.id);
  };

  const togglePin = async (noteId, pinned) => {
    await fetch(`${API_URL}/api/crm/notes/${noteId}`, { method: 'PATCH', headers, body: JSON.stringify({ is_pinned: !pinned }) });
    fetchStaffNotes(selectedStaff.id);
  };

  const openStaffCard = (s) => {
    setSelectedStaff(s);
    fetchStaffNotes(s.id);
  };

  const shiftWeek = (dir) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + (dir * 7));
    setWeekStart(d.toISOString().split('T')[0]);
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.name || '';
  const statusColor = (s) => s === 'approved' ? 'bg-green-500/20 text-green-400' : s === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400';
  const roleColors = { super_admin: 'bg-violet-500/20 text-violet-400', admin: 'bg-blue-500/20 text-blue-400', manager: 'bg-cyan-500/20 text-cyan-400', staff: 'bg-gray-500/20 text-gray-400' };

  const tabs = [
    { id: 'staff', label: 'Staff', icon: Users },
    { id: 'shifts', label: 'Shifts', icon: Clock },
    { id: 'rota', label: 'Rota', icon: Calendar },
    { id: 'holidays', label: 'Holidays', icon: Umbrella },
  ];

  return (
    <div className="p-4 lg:p-8" data-testid="staff-management-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-uf-text">Staff Management</h1>
          <p className="text-uf-text-muted text-sm mt-1">Staff accounts, rotas, shifts & holidays</p>
        </div>
        <select data-testid="site-filter" value={selectedSite} onChange={e => setSelectedSite(e.target.value)} className="bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="flex gap-1 mb-6 bg-uf-surface rounded-lg p-1 overflow-x-auto" data-testid="staff-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === t.id ? 'bg-uf-red text-white' : 'text-uf-text-muted hover:text-uf-text'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* ==================== STAFF TAB ==================== */}
      {activeTab === 'staff' && (
        <div className="flex gap-4">
          <div className={`${selectedStaff ? 'w-5/12' : 'w-full'} transition-all`}>
            <div className="flex gap-3 mb-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-uf-text-muted" />
                <input data-testid="staff-search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search staff..."
                  className="w-full pl-10 pr-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text text-sm" />
              </div>
              {isManager && (
                <PermissionGate perm="staff.create">
                  <button data-testid="add-staff-btn" onClick={() => setShowAddStaff(true)} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm hover:bg-uf-red/90">
                    <Plus className="w-4 h-4" />Add Staff
                  </button>
                </PermissionGate>
              )}
            </div>
            <div className="bg-uf-surface rounded-xl border border-uf-border overflow-hidden">
              <div className="divide-y divide-uf-border/50 max-h-[600px] overflow-y-auto">
                {staff.map(s => (
                  <div key={s.id} onClick={() => openStaffCard(s)} data-testid={`staff-row-${s.id}`}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-uf-surface-hi transition-colors ${selectedStaff?.id === s.id ? 'bg-uf-surface-hi' : ''}`}>
                    <div className="w-10 h-10 rounded-full bg-uf-red/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-uf-red">{s.first_name?.[0]}{s.last_name?.[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-uf-text font-medium text-sm truncate">{s.first_name} {s.last_name}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${roleColors[s.role] || roleColors.staff}`}>{s.role?.replace('_', ' ')}</span>
                      </div>
                      <p className="text-uf-text-muted text-xs truncate">{s.email}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {s.employee_number && <span className="text-uf-text-muted text-xs flex items-center gap-1"><Hash className="w-3 h-3" />{s.employee_number}</span>}
                      {!s.is_active && <span className="text-red-400 text-[10px]">Inactive</span>}
                    </div>
                  </div>
                ))}
                {!staff.length && <div className="p-8 text-center text-uf-text-muted text-sm">No staff found</div>}
              </div>
            </div>
          </div>

          {/* Staff Contact Card */}
          {selectedStaff && (
            <div className="w-7/12 bg-uf-surface rounded-xl border border-uf-border overflow-hidden" data-testid="staff-card">
              <div className="p-5 border-b border-uf-border">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-uf-red/20 ring-2 ring-uf-red/30 flex items-center justify-center">
                      <span className="text-xl font-bold text-uf-red">{selectedStaff.first_name?.[0]}{selectedStaff.last_name?.[0]}</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-uf-text" data-testid="staff-card-name">{selectedStaff.first_name} {selectedStaff.last_name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${roleColors[selectedStaff.role] || roleColors.staff}`}>{selectedStaff.role?.replace('_', ' ')}</span>
                        {selectedStaff.employee_number && <span className="text-uf-text-muted text-xs flex items-center gap-1"><Hash className="w-3 h-3" />{selectedStaff.employee_number}</span>}
                        {selectedStaff.is_active ? <span className="text-emerald-400 text-[10px]">Active</span> : <span className="text-red-400 text-[10px]">Inactive</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {isManager && (
                      <>
                        <PermissionGate perm="staff.edit"><button onClick={() => setEditingStaff({...selectedStaff})} className="p-1.5 text-uf-text-muted hover:text-blue-400" title="Edit Details" data-testid="edit-staff-btn"><Edit3 className="w-4 h-4" /></button></PermissionGate>
                        <button onClick={() => setShowQR(selectedStaff)} className="p-1.5 text-uf-text-muted hover:text-uf-text" title="Show QR"><QrCode className="w-4 h-4" /></button>
                        <PermissionGate perm="staff.assign_roles"><button onClick={() => setShowPermModal(selectedStaff)} className="p-1.5 text-uf-text-muted hover:text-uf-text" title="Permissions"><Shield className="w-4 h-4" /></button></PermissionGate>
                        <PermissionGate perm="staff.edit"><button onClick={() => forcePasswordReset(selectedStaff.id)} className="p-1.5 text-uf-text-muted hover:text-amber-400" title="Force Password Reset" data-testid="force-password-reset-btn"><Key className="w-4 h-4" /></button></PermissionGate>
                        <PermissionGate perm="staff.delete"><button onClick={() => deactivateStaff(selectedStaff.id)} className="p-1.5 text-uf-text-muted hover:text-red-400" title="Deactivate" data-testid="deactivate-staff-btn"><Trash2 className="w-4 h-4" /></button></PermissionGate>
                      </>
                    )}
                    <button onClick={() => setSelectedStaff(null)} className="p-1.5 text-uf-text-muted hover:text-uf-text"><X className="w-5 h-5" /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 mt-3 text-xs">
                  {selectedStaff.email && <span className="flex items-center gap-1.5 text-uf-text-muted"><Mail className="w-3.5 h-3.5 text-uf-red" />{selectedStaff.email}</span>}
                  {selectedStaff.phone && <span className="flex items-center gap-1.5 text-uf-text-muted"><Phone className="w-3.5 h-3.5 text-uf-red" />{selectedStaff.phone}</span>}
                  {getSiteName(selectedStaff.site_id) && <span className="flex items-center gap-1.5 text-uf-text-muted"><MapPin className="w-3.5 h-3.5 text-uf-red" />{getSiteName(selectedStaff.site_id)}</span>}
                </div>
                {(selectedStaff.permissions || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {selectedStaff.permissions.map(p => <span key={p} className="px-1.5 py-0.5 bg-uf-surface-hi rounded text-[10px] text-uf-text-muted">{p.replace(/_/g, ' ')}</span>)}
                  </div>
                )}
              </div>

              {/* Edit Staff Form */}
              {editingStaff && editingStaff.id === selectedStaff.id && (
                <div className="p-4 border-b border-uf-border bg-uf-surface-hi space-y-3" data-testid="edit-staff-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] text-uf-text-muted uppercase">First Name</label>
                      <input value={editingStaff.first_name || ''} onChange={e => setEditingStaff({...editingStaff, first_name: e.target.value})}
                        className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-uf-text-muted uppercase">Last Name</label>
                      <input value={editingStaff.last_name || ''} onChange={e => setEditingStaff({...editingStaff, last_name: e.target.value})}
                        className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-uf-text-muted uppercase">Phone</label>
                      <input value={editingStaff.phone || ''} onChange={e => setEditingStaff({...editingStaff, phone: e.target.value})}
                        className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-uf-text-muted uppercase">Role</label>
                      <select value={editingStaff.role || 'staff'} onChange={e => setEditingStaff({...editingStaff, role: e.target.value})}
                        className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="edit-staff-role">
                        <option value="staff">Staff</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] text-uf-text-muted uppercase">Site</label>
                      <select value={editingStaff.site_id || ''} onChange={e => setEditingStaff({...editingStaff, site_id: e.target.value})}
                        className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" data-testid="edit-staff-site">
                        <option value="">All Sites</option>
                        {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditingStaff(null)} className="px-3 py-1.5 text-uf-text-muted text-xs">Cancel</button>
                    <button onClick={saveStaffEdit} className="px-4 py-1.5 bg-uf-red text-white rounded-lg text-xs" data-testid="save-staff-edit-btn">Save Changes</button>
                  </div>
                </div>
              )}

              {/* Notes Section */}
              <div className="p-5 overflow-y-auto max-h-[400px]">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-xs font-semibold text-uf-text uppercase tracking-wider">Notes</p>
                  <button data-testid="add-staff-note-btn" onClick={() => setShowAddNote(true)}
                    className="flex items-center gap-1.5 bg-uf-red/10 text-uf-red px-3 py-1.5 rounded-lg text-xs hover:bg-uf-red/20">
                    <Plus className="w-3 h-3" />Add Note
                  </button>
                </div>

                {showAddNote && (
                  <div className="mb-3 bg-uf-surface-hi rounded-lg border border-uf-border p-3 space-y-2">
                    <select value={noteType} onChange={e => setNoteType(e.target.value)} className="bg-uf-surface border border-uf-border rounded px-2 py-1.5 text-uf-text text-xs w-full">
                      {['general','call','meeting','follow_up','complaint','feedback'].map(t => <option key={t} value={t}>{t.replace('_',' ')}</option>)}
                    </select>
                    <textarea data-testid="staff-note-input" value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Write a note..." rows={3}
                      className="w-full bg-uf-surface border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm resize-none" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => { setShowAddNote(false); setNoteContent(''); }} className="px-3 py-1.5 text-uf-text-muted text-xs">Cancel</button>
                      <button data-testid="save-staff-note-btn" onClick={addNote} className="px-3 py-1.5 bg-uf-red text-white rounded-lg text-xs">Save</button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {staffNotes.map(n => (
                    <div key={n.id} className={`rounded-lg border p-3 ${n.is_pinned ? 'border-amber-500/30 bg-amber-500/5' : 'border-uf-border bg-uf-surface-hi'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 text-[10px]">
                            <span className={`px-1.5 py-0.5 rounded capitalize ${n.note_type === 'call' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>{n.note_type?.replace('_',' ')}</span>
                            {n.is_pinned && <Pin className="w-2.5 h-2.5 text-amber-400" />}
                            <span className="text-uf-text-muted">{n.created_at?.replace('T',' ').slice(0,16)}</span>
                            {n.staff_name && <span className="text-uf-text-muted">by {n.staff_name}</span>}
                          </div>
                          <p className="text-uf-text text-sm whitespace-pre-wrap">{n.content}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => togglePin(n.id, n.is_pinned)} className="p-1 text-uf-text-muted hover:text-amber-400">{n.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}</button>
                          <button onClick={() => deleteNote(n.id)} className="p-1 text-uf-text-muted hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!staffNotes.length && !showAddNote && <p className="text-uf-text-muted text-xs text-center py-4">No notes yet</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== SHIFTS TAB ==================== */}
      {activeTab === 'shifts' && (
        <div data-testid="shifts-section">
          {isManager && <div className="flex justify-end mb-4"><button onClick={() => setShowAddShift(true)} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm" data-testid="add-shift-btn"><Plus className="w-4 h-4" />Add Shift</button></div>}
          {!selectedSite && <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted">Select a site to manage shifts</div>}
          {selectedSite && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {shifts.map(s => (
                <div key={s.id} className="bg-uf-surface rounded-xl border border-uf-border p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-uf-text font-semibold">{s.name}</h4>
                      <p className="text-uf-text-muted text-sm mt-1">{s.start_time} - {s.end_time}</p>
                    </div>
                    {isManager && (
                      <button onClick={() => deleteShift(s.id)} className="p-1 text-uf-text-muted hover:text-red-400" title="Delete Shift" data-testid={`delete-shift-${s.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {!shifts.length && <div className="col-span-3 bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted">No shifts for this site</div>}
            </div>
          )}
        </div>
      )}

      {/* ==================== ROTA TAB ==================== */}
      {activeTab === 'rota' && (
        <div data-testid="rota-section">
          {!selectedSite && <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted">Select a site to view rota</div>}
          {selectedSite && (
            <>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => shiftWeek(-1)} className="p-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text-muted hover:text-uf-text"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-uf-text text-sm font-medium">Week of {weekStart}</span>
                <div className="flex gap-2">
                  <button onClick={() => shiftWeek(1)} className="p-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text-muted hover:text-uf-text"><ChevronRight className="w-4 h-4" /></button>
                  {isManager && <button onClick={() => setShowAddRota(true)} className="flex items-center gap-2 bg-uf-red text-white px-3 py-2 rounded-lg text-xs" data-testid="add-rota-btn"><Plus className="w-3 h-3" />Add Entry</button>}
                </div>
              </div>
              <div className="bg-uf-surface rounded-xl border border-uf-border overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-uf-border text-uf-text-muted">
                    <th className="p-3 text-left">Staff</th>
                    {weekDays.map((d,i) => <th key={i} className="p-3 text-center">{d.toLocaleDateString('en-GB',{weekday:'short',day:'numeric'})}</th>)}
                  </tr></thead>
                  <tbody>
                    {staff.map(s => (
                      <tr key={s.id} className="border-b border-uf-border/50">
                        <td className="p-3 text-uf-text font-medium whitespace-nowrap">{s.first_name} {s.last_name}</td>
                        {weekDays.map((d,i) => {
                          const dateStr = d.toISOString().split('T')[0];
                          const entry = rota.find(r => r.staff_id === s.id && r.date === dateStr);
                          return (
                            <td key={i} className="p-3 text-center">
                              {entry ? (
                                <div className="bg-uf-red/10 text-uf-red px-2 py-1 rounded text-[10px] relative group">
                                  {entry.shift_name || `${entry.start_time}-${entry.end_time}`}
                                  {isManager && <button onClick={() => deleteRotaEntry(entry.id)} className="hidden group-hover:block absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[8px]">x</button>}
                                </div>
                              ) : <span className="text-uf-text-muted">-</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ==================== HOLIDAYS TAB ==================== */}
      {activeTab === 'holidays' && (
        <div data-testid="holidays-section">
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowAddHoliday(true)} className="flex items-center gap-2 bg-uf-red text-white px-4 py-2 rounded-lg text-sm" data-testid="add-holiday-btn"><Plus className="w-4 h-4" />Request Holiday</button>
          </div>
          <div className="space-y-3">
            {holidays.map(h => (
              <div key={h.id} className="bg-uf-surface rounded-xl border border-uf-border p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0"><Umbrella className="w-5 h-5 text-cyan-400" /></div>
                <div className="flex-1">
                  <p className="text-uf-text font-medium text-sm">{h.staff_name}</p>
                  <p className="text-uf-text-muted text-xs">{h.start_date} to {h.end_date} {h.reason && `- ${h.reason}`}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs capitalize ${statusColor(h.status)}`}>{h.status}</span>
                {isManager && h.status === 'pending' && (
                  <div className="flex gap-1">
                    <button onClick={() => reviewHoliday(h.id, 'approve')} className="p-1.5 bg-green-500/10 text-green-400 rounded hover:bg-green-500/20"><Check className="w-4 h-4" /></button>
                    <button onClick={() => reviewHoliday(h.id, 'reject')} className="p-1.5 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            ))}
            {!holidays.length && <div className="bg-uf-surface rounded-xl border border-uf-border p-8 text-center text-uf-text-muted">No holiday requests</div>}
          </div>
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Add Staff Modal */}
      {showAddStaff && (
        <Modal title="Add Staff Member" onClose={() => setShowAddStaff(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="new-staff-first" placeholder="First Name *" value={newStaff.first_name} onChange={e => setNewStaff({...newStaff, first_name: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
              <input placeholder="Last Name *" value={newStaff.last_name} onChange={e => setNewStaff({...newStaff, last_name: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            </div>
            <input data-testid="new-staff-email" placeholder="Email *" value={newStaff.email} onChange={e => setNewStaff({...newStaff, email: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <input placeholder="Phone" value={newStaff.phone} onChange={e => setNewStaff({...newStaff, phone: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <select value={newStaff.role} onChange={e => setNewStaff({...newStaff, role: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
                <option value="staff">Staff</option><option value="manager">Manager</option><option value="admin">Admin</option><option value="super_admin">Super Admin</option>
              </select>
              <select value={newStaff.site_id} onChange={e => setNewStaff({...newStaff, site_id: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
                <option value="">Assign Site</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <p className="text-uf-text-muted text-xs">A temporary password will be generated. The staff member will receive a welcome email to set their own password.</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowAddStaff(false)} className="flex-1 border border-uf-border text-uf-text py-2 rounded-lg text-sm">Cancel</button>
              <button data-testid="save-staff-btn" onClick={createStaffMember} className="flex-1 bg-uf-red text-white py-2 rounded-lg text-sm">Create Staff</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Shift Modal */}
      {showAddShift && (
        <Modal title="Add Shift Pattern" onClose={() => setShowAddShift(false)}>
          <div className="space-y-3">
            <input placeholder="Shift Name" value={newShift.name} onChange={e => setNewShift({...newShift, name: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <input type="time" value={newShift.start_time} onChange={e => setNewShift({...newShift, start_time: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
              <input type="time" value={newShift.end_time} onChange={e => setNewShift({...newShift, end_time: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowAddShift(false)} className="flex-1 border border-uf-border text-uf-text py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={createShift} className="flex-1 bg-uf-red text-white py-2 rounded-lg text-sm">Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Add Rota Modal */}
      {showAddRota && (
        <Modal title="Add Rota Entry" onClose={() => setShowAddRota(false)}>
          <div className="space-y-3">
            <select value={newRota.staff_id} onChange={e => setNewRota({...newRota, staff_id: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
              <option value="">Select Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
            <select value={newRota.shift_id} onChange={e => setNewRota({...newRota, shift_id: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm">
              <option value="">Select Shift</option>
              {shifts.map(s => <option key={s.id} value={s.id}>{s.name} ({s.start_time}-{s.end_time})</option>)}
            </select>
            <input type="date" value={newRota.date} onChange={e => setNewRota({...newRota, date: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <input placeholder="Notes" value={newRota.notes} onChange={e => setNewRota({...newRota, notes: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowAddRota(false)} className="flex-1 border border-uf-border text-uf-text py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={createRotaEntry} className="flex-1 bg-uf-red text-white py-2 rounded-lg text-sm">Save</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Holiday Request Modal */}
      {showAddHoliday && (
        <Modal title="Request Holiday" onClose={() => setShowAddHoliday(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={newHoliday.start_date} onChange={e => setNewHoliday({...newHoliday, start_date: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
              <input type="date" value={newHoliday.end_date} onChange={e => setNewHoliday({...newHoliday, end_date: e.target.value})} className="bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            </div>
            <input placeholder="Reason" value={newHoliday.reason} onChange={e => setNewHoliday({...newHoliday, reason: e.target.value})} className="w-full bg-uf-surface-hi border border-uf-border rounded-lg px-3 py-2 text-uf-text text-sm" />
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowAddHoliday(false)} className="flex-1 border border-uf-border text-uf-text py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={createHolidayRequest} className="flex-1 bg-uf-red text-white py-2 rounded-lg text-sm">Submit</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Permissions Modal */}
      {showPermModal && (
        <PermissionsModal staff={showPermModal} allPermissions={ALL_PERMISSIONS} onSave={updatePermissions} onClose={() => setShowPermModal(null)} />
      )}

      {/* QR Code Modal */}
      {showQR && (
        <Modal title={`${showQR.first_name}'s Access QR`} onClose={() => setShowQR(null)}>
          <div className="flex flex-col items-center py-4">
            <div className="bg-white p-4 rounded-xl">
              <QRCode value={JSON.stringify({ type: 'staff', employee_number: showQR.employee_number, id: showQR.id })} size={200} />
            </div>
            <p className="text-uf-text font-bold text-lg mt-4">{showQR.employee_number}</p>
            <p className="text-uf-text-muted text-sm">{showQR.first_name} {showQR.last_name}</p>
            <p className="text-uf-text-muted text-xs mt-1">{showQR.role?.replace('_',' ')}</p>
          </div>
        </Modal>
      )}
    </div>
  );
};

const PermissionsModal = ({ staff, allPermissions, onSave, onClose }) => {
  const [perms, setPerms] = useState(staff.permissions || []);
  const isSuperAdmin = staff.role === 'super_admin';
  const groups = [...new Set(allPermissions.map(p => p.group))];
  const toggle = (key) => setPerms(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);

  return (
    <Modal title={`Permissions - ${staff.first_name} ${staff.last_name}`} onClose={onClose}>
      {isSuperAdmin && <p className="text-amber-400 text-xs mb-3 bg-amber-500/10 rounded p-2">Super admin has all permissions by default</p>}
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {groups.map(g => (
          <div key={g}>
            <p className="text-uf-text-muted text-xs uppercase tracking-wider font-semibold mb-2">{g}</p>
            <div className="space-y-1">
              {allPermissions.filter(p => p.group === g).map(p => (
                <label key={p.key} className="flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded hover:bg-uf-surface-hi">
                  <input type="checkbox" checked={isSuperAdmin || perms.includes(p.key)} onChange={() => !isSuperAdmin && toggle(p.key)} disabled={isSuperAdmin} className="accent-uf-red" />
                  <span className="text-uf-text text-sm">{p.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3 pt-4">
        <button onClick={onClose} className="flex-1 border border-uf-border text-uf-text py-2 rounded-lg text-sm">Cancel</button>
        <button onClick={() => onSave(staff.id, perms)} className="flex-1 bg-uf-red text-white py-2 rounded-lg text-sm" disabled={isSuperAdmin}>Save Permissions</button>
      </div>
    </Modal>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
    <div className="bg-uf-surface rounded-xl border border-uf-border p-6 w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-uf-text">{title}</h3>
        <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default AdminStaffManagement;
